import React, { useContext } from 'react';
import { Stack, Select, MenuItem, Button } from '@mui/material';
import { QuickSearch } from 'components/_atoms';
import PageToolbar from '_lex/_atoms/PageToolbar';
import { translate as t } from 'hooks/useTranslations';
import { AppContext } from 'views/App/context';

type IssuedManualsUrlParams = {
  search?: string | null;
  orgId?: string | null;
  jurisdictionId?: string | null;
};

type FilterOption = {
  value: string;
  label: string;
};

export interface IssuedManualsPolicyPageToolbarProps {
  params: IssuedManualsUrlParams;
  updateParams: (updatedParams: Partial<IssuedManualsUrlParams>) => void;
  isFiltered: boolean;
  clearParams: () => void;
  jurisdictionOptions: FilterOption[];
}

const IssuedManualsPolicyPageToolbar: React.FC<IssuedManualsPolicyPageToolbarProps> = ({
  params,
  updateParams,
  isFiltered,
  clearParams,
  jurisdictionOptions,
}) => {

  const { currentProfile, isLexipolStaff } = useContext(AppContext);

  // Only fetch organizations and jurisdictions for risk pool users
  const isRiskPoolUser = currentProfile?.organization?.riskPoolEnabled;

  // Hide toolbar when it would be empty (no search/filters for this user type)
  const showToolbar = isFiltered || isLexipolStaff;
  if (!showToolbar) {
    return null;
  }

  return (
    <PageToolbar
      leftItems={
        <Stack
          direction='row'
          sx={{
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2,
            '& > *:not(:last-child)': {
              marginRight: 3,
            },
            '& > *': {
              minWidth: 'fit-content',
            },
          }}
        >
          {isLexipolStaff && (
            <>
              <QuickSearch
                id='org_keyword_search'
                initialValue={params.search || ''}
                onChange={(val) => updateParams({ search: val })}
                placeholder={t('policy.issuedManuals.search.placeholder')}
                sx={{ width: 220, minWidth: 220, maxWidth: 220 }}
              />
              {isRiskPoolUser && (
                <>
                  {/* Temporarily hidden - organization endpoint not ready
                    <Select
                      id="organization-filter"
                      size="small"
                      label={false}
                      value={params.orgId || ''}
                      onChange={({ target }) => updateParams({ orgId: target.value })}
                      displayEmpty
                      sx={{
                        width: 220,
                        minWidth: 220,
                        maxWidth: 220,
                        '& fieldset': {
                          top: 0,
                        },
                        '& legend': {
                          display: 'none',
                        },
                      }}
                      renderValue={(selected) => {
                        if (!selected) {
                          return t('policy.issuedManuals.filters.searchByOrganization') + ': All';
                        }
                        const org = organizationOptions.find((o) => o.value === selected);
                        return t('policy.issuedManuals.filters.searchByOrganization') + ': ' + (org?.label || selected);
                      }}
                    >
                      <MenuItem value="">
                        {t('common.all')}
                      </MenuItem>
                      {organizationOptions.map((org) => (
                        <MenuItem key={org.value} value={org.value}>
                          {org.label}
                        </MenuItem>
                      ))}
                    </Select>
                    */}
                  <Select
                    id="jurisdiction-filter"
                    size="small"
                    label={false}
                    value={params.jurisdictionId || ''}
                    onChange={({ target }) => updateParams({ jurisdictionId: target.value })}
                    displayEmpty
                    sx={{
                      width: 220,
                      minWidth: 220,
                      maxWidth: 220,
                      '& fieldset': {
                        top: 0,
                      },
                      '& legend': {
                        display: 'none',
                      },
                    }}
                    renderValue={(selected) => {
                      if (!selected) {
                        return t('policy.issuedManuals.filters.searchByJurisdiction') + ': All';
                      }
                      const jurisdiction = jurisdictionOptions.find((j) => j.value === selected);
                      return t('policy.issuedManuals.filters.searchByJurisdiction') + ': ' + (jurisdiction?.label || selected);
                    }}
                  >
                    <MenuItem value="">
                      {t('common.all')}
                    </MenuItem>
                    {jurisdictionOptions.map((jurisdiction) => (
                      <MenuItem key={jurisdiction.value} value={jurisdiction.value}>
                        {jurisdiction.label}
                      </MenuItem>
                    ))}
                  </Select>
                </>
              )}
            </>
          )}
          {isFiltered && (
            <Button
              color='neutral'
              onClick={clearParams}
              size='small'
              sx={{
                whiteSpace: 'nowrap',
                minWidth: '80px',
                px: 2,
              }}
            >
              {t('common.clearFilters')}
            </Button>
          )}
        </Stack>
      }
    />
  );
};

export default IssuedManualsPolicyPageToolbar;
